import { useState } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { fmtTL, fmtDate } from "../../lib/format";
import AmountInput from "../AmountInput";
import WageTypeHistory, { effectiveOf, typeOf } from "./WageTypeHistory";
import { cls, Row } from "./personnelShared";

export const DEFAULT_WAGE_TYPES = ["MAAŞ", "YEMEK", "KAHVALTI", "YOL", "GÖREV YERİ TAZMİNATI", "ÇOCUK PARASI +6 YAŞ", "ÇOCUK PARASI 0-6 YAŞ", "ÖĞRENİM YARDIMI", "AİLE YARDIMI", "DİL TAZMİNATI", "ŞUBE TAZMİNATI", "KIDEM PRİMİ", "EK ÖDEME-PART+"];
export const WAGE_BASES = ["Aylık", "Günlük", "Saatlik"];
const TH = "text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold";
const tlOrDash = (v) => (v ? fmtTL(v) : <span className="text-slate-400">—</span>);

// Yeni (kaydedilmemiş) kartta MAAŞ satırı doğrudan form alanlarına bağlı — kaydedilince "İlk ücret" olarak geçmişe yazılır
function NewCardMaasRow({ form, setForm }) {
  return (
    <tr className="border-t border-slate-100" data-testid="wage-type-row-MAAŞ">
      <td className="px-3 py-1.5 text-xs font-bold">MAAŞ</td>
      <td className="px-2 py-1"><select value={form.wage_basis || "Aylık"} onChange={(e) => setForm({ ...form, wage_basis: e.target.value })} data-testid="pf-wage-basis" className={cls}>{WAGE_BASES.map((b) => <option key={b} value={b}>{b}</option>)}</select></td>
      <td className="px-2 py-1"><AmountInput value={form.gross_salary} onChange={(v) => setForm({ ...form, gross_salary: v })} testid="pf-gross" className={`${cls} text-right`} placeholder="Brüt" /></td>
      <td className="px-2 py-1"><AmountInput value={form.net_salary} onChange={(v) => setForm({ ...form, net_salary: v })} testid="pf-net" className={`${cls} text-right`} placeholder="Net" /></td>
      <td className="px-3 py-1.5 text-xs text-slate-600">{form.hire_date ? fmtDate(form.hire_date) : "İşe giriş tarihi"}</td>
      <td className="px-3 py-1.5 text-xs text-slate-500">İlk ücret — kart kaydedilince geçmişe yazılır</td>
    </tr>
  );
}

// Türün bugün yürürlükteki değeri; MAAŞ geçmişte yoksa karttaki brüt/net ("Kart ücreti")
function currentOf(person, type) {
  const eff = effectiveOf(person?.wage_history, type);
  const legacy = type === "MAAŞ" && !eff && (person?.gross_salary || person?.net_salary) ? { gross_salary: person.gross_salary, net_salary: person.net_salary, valid_from: person.hire_date, note: "Kart ücreti" } : null;
  return eff || legacy;
}

// Başlığın hemen altı: tüm türlerin brüt / net toplamı (yürürlükteki değerler)
function TotalsRow({ gross, net }) {
  return (
    <tr className="border-t border-b-2 border-slate-300 bg-amber-50/70" data-testid="wage-totals-row">
      <td className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-800" colSpan={2}>Toplam</td>
      <td className="px-3 py-2 text-xs text-right font-bold text-slate-900" data-testid="wage-total-gross">{fmtTL(gross || 0)}</td>
      <td className="px-3 py-2 text-xs text-right font-bold text-slate-900" data-testid="wage-total-net">{fmtTL(net || 0)}</td>
      <td className="px-3 py-2 text-[11px] text-slate-500" colSpan={2}>tüm türlerin toplamı</td>
    </tr>
  );
}

function TypeRow({ type, person, open, onToggle, isAdmin, onChanged }) {
  const cur = currentOf(person, type);
  const count = (person?.wage_history || []).filter((w) => typeOf(w) === type).length;
  return (
    <>
      <tr onClick={onToggle} data-testid={`wage-type-row-${type}`} aria-expanded={open} title="Tıkla: tüm değişiklikleri sırasıyla gör / yeni değişiklik ekle"
        className={`border-t border-slate-100 cursor-pointer transition-colors ${open ? "bg-orange-50" : "hover:bg-slate-50"}`}>
        <td className="px-3 py-2 text-xs font-bold whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />{type}{count > 1 && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-semibold" data-testid={`wage-type-count-${type}`}>{count}</span>}</span></td>
        <td className="px-3 py-2 text-xs" data-testid={`wage-type-basis-${type}`}>{cur ? (cur.wage_basis || "Aylık") : <span className="text-slate-400">—</span>}</td>
        <td className="px-3 py-2 text-xs text-right font-semibold" data-testid={`wage-type-gross-${type}`}>{tlOrDash(cur?.gross_salary)}</td>
        <td className="px-3 py-2 text-xs text-right font-semibold" data-testid={`wage-type-net-${type}`}>{tlOrDash(cur?.net_salary)}</td>
        <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`wage-type-date-${type}`}>{cur?.valid_from ? fmtDate(cur.valid_from) : <span className="text-slate-400">—</span>}</td>
        <td className="px-3 py-2 text-xs text-slate-600">{cur?.note || <span className="text-slate-400">—</span>}</td>
      </tr>
      {open && <tr className="bg-orange-50/40"><td colSpan={6} className="px-3 py-3"><WageTypeHistory person={person} type={type} isAdmin={isAdmin} onChanged={onChanged} /></td></tr>}
    </>
  );
}

function AddTypeRow({ suggestions, onAdd }) {
  const [v, setV] = useState("");
  const submit = () => { const t = v.trim().toLocaleUpperCase("tr"); if (!t) return; onAdd(t); setV(""); };
  return (
    <tr className="border-t border-slate-200 bg-slate-50/60">
      <td colSpan={6} className="px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Plus className="w-3.5 h-3.5 text-slate-500" />
          <input list="wage-type-suggestions" value={v} onChange={(e) => setV(e.target.value.toLocaleUpperCase("tr"))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); submit(); } }}
            placeholder="TÜR EKLE — örn. PRİM, İKRAMİYE" data-testid="wage-type-new" className={`${cls} !w-72`} />
          <datalist id="wage-type-suggestions">{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
          <button type="button" onClick={submit} data-testid="wage-type-add" className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold hover:bg-slate-100">+ Tür Ekle</button>
        </div>
      </td>
    </tr>
  );
}

// Ücret sekmesi: tür bazlı tablo (TÜRÜ · BRÜT · NET · GEÇERLİLİK · AÇIKLAMA) — satıra tıklayınca tüm değişiklikler sırasıyla; hesaplamalar geçerlilik tarihine göre
export default function PersonnelWage({ form, setForm, person, viewOnly, isAdmin, onChanged, typeSuggestions = [] }) {
  const [open, setOpen] = useState(null);
  const [extra, setExtra] = useState([]);
  const isNew = !person?.id;
  const types = [...new Set([...DEFAULT_WAGE_TYPES, ...(person?.wage_history || []).map(typeOf), ...extra])];
  const addType = (t) => { if (!types.includes(t)) setExtra((xs) => [...xs, t]); setOpen(t); };
  const sum = (k) => (isNew ? Number(form[k]) || 0 : types.reduce((a, t) => a + (Number(currentOf(person, t)?.[k]) || 0), 0));
  return (
    <div className="space-y-4" data-testid="pf-wage-tab">
      <div className="text-sm font-bold text-indigo-600 border-b border-slate-200 pb-1">Güncel Ücret <span className="text-[10px] font-normal text-slate-500">(bugün yürürlükteki değerler · satıra tıklayın: tüm değişiklikler sırasıyla · Bordro ve puantaj geçerlilik tarihine göre hesaplar)</span></div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="tbl text-xs w-full" data-testid="wage-type-table">
          <thead><tr className="bg-slate-50"><th className={TH}>Türü</th><th className={TH}>Ücret Şekli</th><th className={`${TH} text-right`}>Brüt</th><th className={`${TH} text-right`}>Net</th><th className={TH}>Ücret Geçerlilik Tarihi</th><th className={TH}>Açıklama</th></tr></thead>
          <tbody>
            <TotalsRow gross={sum("gross_salary")} net={sum("net_salary")} />
            {isNew ? (
              <>
                <NewCardMaasRow form={form} setForm={setForm} />
                <tr className="border-t border-slate-100"><td colSpan={6} className="px-3 py-2 text-[11px] text-slate-500" data-testid="wage-new-hint">YEMEK, KAHVALTI, YOL, tazminat / yardım / prim türleri kart kaydedildikten sonra bu tablodan girilir.</td></tr>
              </>
            ) : (
              <>
                {types.map((t) => <TypeRow key={t} type={t} person={person} open={open === t} onToggle={() => setOpen(open === t ? null : t)} isAdmin={isAdmin} onChanged={onChanged} />)}
                {!viewOnly && <AddTypeRow suggestions={typeSuggestions.filter((s) => !types.includes(s))} onAdd={addType} />}
              </>
            )}
          </tbody>
        </table>
      </div>
      <fieldset disabled={viewOnly} className={`xl:w-1/2 ${viewOnly ? "[&_input]:bg-slate-50" : ""}`}>
        <Row label="Devir Vergi Matrahı" testid="pf-carryover-row"><AmountInput value={form.carryover_tax_base} onChange={(v) => setForm({ ...form, carryover_tax_base: v })} testid="pf-carryover-tax-base" className={`${cls} text-right`} placeholder="Önceki işyerinden devreden kümülatif gelir vergisi matrahı" title="Yıl içinde başka işverenden gelen personelde, önceki işyerinde oluşan kümülatif gelir vergisi matrahı" /></Row>
      </fieldset>
    </div>
  );
}
